import React, { useCallback, useEffect, useMemo, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ListItemIcon, Menu, MenuItem, Popover, TextField, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import TagsDropdown from '../TagsDropdown';
import { useWebSocket } from '../WebSocketContext';
import { useDashboard } from '../Dashboard/DashboardContext';
import { systemTagsEvent, systemTagsGlobal } from '../../constants/systemTags';
import { UnifiedAnswerRenderer } from '../Common';

const escapeHtml = (text) => {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

const getTagValueForLookup = (tag) => {
  if (!tag) return '';
  const candidate = tag?.variable ?? tag?.value ?? tag?.key ?? tag?.name ?? tag?.label ?? tag;
  if (candidate == null) return '';
  if (typeof candidate === 'string') return candidate.trim();
  return String(candidate).trim();
};

const getTagLookupCandidates = (tag) => {
  if (!tag) return [];
  return [
    tag?.variable,
    tag?.value,
    tag?.key,
    tag?.name,
    tag?.label
  ]
    .filter((candidate) => typeof candidate === 'string' && candidate.trim().length > 0)
    .map((candidate) => candidate.trim());
};

const standaloneTagPattern = /^\s*(\{\{([a-zA-Z0-9_]+)\}\}|\{([a-zA-Z0-9_]+)\})\s*$/;

const trimStandalonePastedTag = (text) => {
  const matchableText = String(text || '').replace(/\u00A0/g, ' ');
  return standaloneTagPattern.test(matchableText) ? matchableText.trim() : text;
};

const getStandalonePastedTag = (text) => {
  const matchableText = String(text || '').replace(/\u00A0/g, ' ');
  const match = matchableText.match(standaloneTagPattern);
  if (!match) return null;
  return {
    tagName: match[2] || match[3],
    escaped: Boolean(match[2])
  };
};

const normalizeTagsInput = (tagsInput) => {
  if (!tagsInput) return [];
  if (typeof tagsInput === 'string') {
    try {
      const parsed = JSON.parse(tagsInput);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      return [tagsInput];
    }
  }
  if (Array.isArray(tagsInput)) {
    return tagsInput;
  }
  if (typeof tagsInput === 'object') {
    return [tagsInput];
  }
  return [];
};

const mergeTagDefinitions = (...tagGroups) => {
  const mergedTags = [];
  const seenValues = new Set();

  tagGroups.forEach((group) => {
    normalizeTagsInput(group).forEach((tag) => {
      const tagValue = getTagValueForLookup(tag);
      const normalizedValue = tagValue.toLowerCase();

      if (normalizedValue && !seenValues.has(normalizedValue)) {
        seenValues.add(normalizedValue);
        mergedTags.push(tag);
        return;
      }

      if (!normalizedValue) {
        mergedTags.push(tag);
      }
    });
  });

  return mergedTags;
};

const TagEditor = forwardRef(({
  value = '',
  onChange,
  placeholder = '',
  onTagClick,
  tags,
  scope,
  caseid,
  className = '',
  singleLine,
  multiline = true,
  disabled = false,
  dense = false,
  minHeight,
  minRows,
  maxRows
}, ref) => {
  const rootRef = useRef(null);
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const dashboard = useDashboard();
  const dashboardTags = dashboard?.tags;
  const [tagMenuAnchorEl, setTagMenuAnchorEl] = useState(null);
  const [triggeredByBrace, setTriggeredByBrace] = useState(false);
  const [tagActionAnchorEl, setTagActionAnchorEl] = useState(null);
  const [tagActionTarget, setTagActionTarget] = useState(null);
  const [escapeDialogOpen, setEscapeDialogOpen] = useState(false);
  const [escapeTarget, setEscapeTarget] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [missingTagDialogOpen, setMissingTagDialogOpen] = useState(false);
  const [missingTagValue, setMissingTagValue] = useState('');
  const [hoverAnchorEl, setHoverAnchorEl] = useState(null);
  const [hoveredTag, setHoveredTag] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);
  const preserveBraceSelectionRef = useRef(false);
  const hoverTimerRef = useRef(null);
  const tagPreviewCacheRef = useRef(new Map());
  const savedSelectionRef = useRef(null);
  const tagDefinitionSignatureRef = useRef('');
  const isSingleLine = typeof singleLine === 'boolean' ? singleLine : !multiline;
  const isDisabled = Boolean(disabled);
  const tagNamePattern = /^[a-zA-Z0-9_]+$/;
  const normalizeForMode = (text) => {
    if (!text) return '';
    if (!isSingleLine) return text;
    return String(text).replace(/\s*\r?\n+\s*/g, ' ');
  };
  const isBlankText = (text) => {
    if (!text) return true;
    return String(text).replace(/\u00A0/g, ' ').trim().length === 0;
  };
  const hasVisibleEditorContent = (root, text) => {
    if (!isBlankText(text)) {
      return true;
    }
    return Boolean(root?.querySelector?.('.tag-chip'));
  };
  const normalizedValue = normalizeForMode(value || '');
  const displayValue = isBlankText(normalizedValue) ? '' : normalizedValue;
  const [isEmpty, setIsEmpty] = useState(isBlankText(displayValue));
  const computedMinHeight = minHeight || (minRows ? `calc(${minRows} * 1.5em + 1.25rem)` : (isSingleLine ? '40px' : '120px'));
  const computedMaxHeight = maxRows ? `calc(${maxRows} * 1.5em + 1.25rem)` : undefined;
  const editorPadding = dense ? 0.5 : 1.25;
  const isApplyingRef = useRef(false);
  const caretMarker = '__TAG_EDITOR_CARET__';
  const tagPattern = /\{[a-zA-Z0-9_]+\}/;
  const tagRegex = /\{\{([a-zA-Z0-9_]+)\}\}|\{([a-zA-Z0-9_]+)\}/g;
  const normalizedTags = useMemo(
    () => mergeTagDefinitions(tags, dashboardTags),
    [tags, dashboardTags]
  );
  const availableTagValueSet = useMemo(() => {
    const set = new Set();
    normalizedTags.forEach((tag) => {
      getTagLookupCandidates(tag).forEach((candidate) => {
        set.add(candidate.toLowerCase());
      });
    });
    [...systemTagsEvent, ...systemTagsGlobal].forEach((tag) => {
      getTagLookupCandidates(tag).forEach((candidate) => {
        set.add(candidate.toLowerCase());
      });
    });
    return set;
  }, [normalizedTags]);
  const hasTagDefinitions = availableTagValueSet.size > 0;
  const isTagMissing = (tagName) => {
    if (!hasTagDefinitions || !tagName) return false;
    return !availableTagValueSet.has(tagName.trim().toLowerCase());
  };
  const getMissingTagTooltip = useCallback(() => (
    t('tag_editor.missing_tag_warning', 'This tag does not exist, the system will treat it as empty')
  ), [t]);
  const tagLookupSignature = useMemo(() => {
    const values = [...availableTagValueSet];
    values.sort();
    return values.join('|');
  }, [availableTagValueSet]);

  const buildTagSpanHtml = (tagName, escaped = false) => {
    const bgColor = generatePastelColor(tagName);
    const color = generateSaturatedColor(tagName);
    const style = escaped
      ? `background-color: transparent; color: ${color}; border: 1px dashed ${color};`
      : `background-color: ${bgColor}; color: ${color};`;
    const escapedAttr = escaped ? ' data-escaped="true"' : '';
    const className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    const missing = !escaped && isTagMissing(tagName);
    const missingAttr = missing ? ' data-missing-tag="true"' : '';
    const warningMessage = missing ? escapeHtml(getMissingTagTooltip(tagName)) : '';
    const labelHtml = `<span class="tag-chip-label">{${escapeHtml(tagName)}}</span>`;
    const warningHtml = missing
      ? `<span class="tag-chip-warning" aria-label="${warningMessage}" title="${warningMessage}">!</span>`
      : '';
    return `<span class="${className}" data-tag="${escapeHtml(tagName)}"${escapedAttr}${missingAttr} contenteditable="false" style="${style}">${labelHtml}${warningHtml}</span>`;
  };

  const closeTagActionMenu = useCallback(() => {
    setTagActionAnchorEl(null);
  }, []);

  const clearTagActionTarget = useCallback(() => {
    setTagActionTarget(null);
  }, []);

  const dispatchEditorInput = useCallback(() => {
    if (!rootRef.current) return;
    const event = new Event('input', { bubbles: true });
    rootRef.current.dispatchEvent(event);
  }, []);

  const focusCaretAfterNode = useCallback((node) => {
    if (!rootRef.current || !node) return;
    rootRef.current.focus();
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const replaceTagNode = (node, tagName, escaped = false) => {
    if (!node || !tagName) return;
    const updatedSpan = buildTagSpan(tagName, escaped);
    node.replaceWith(updatedSpan);
    focusCaretAfterNode(updatedSpan);
    dispatchEditorInput();
  };

  const removeTagNode = useCallback((node) => {
    if (!node) return;
    node.remove();
    if (rootRef.current) {
      rootRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.selectNodeContents(rootRef.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      dispatchEditorInput();
    }
  }, [dispatchEditorInput]);

  // Convert plain text (with `{tag}`) into HTML with span tags
  const toHtml = (text, options = {}) => {
    const { renderMissingTagsAsText = false } = options;
    const normalizedText = normalizeForMode(text);
    if (!normalizedText) return '';
    
    // First, handle newlines by converting them to <br> tags
    // We'll process line by line
    const lines = isSingleLine ? [normalizedText] : normalizedText.split('\n');
    let html = '';
    
    lines.forEach((line, lineIndex) => {
      tagRegex.lastIndex = 0;
      let lastIndex = 0;
      let match;
      
      while ((match = tagRegex.exec(line)) !== null) {
        const idx = match.index;
        if (idx > lastIndex) {
          html += escapeHtml(line.substring(lastIndex, idx));
        }
        const tagName = match[1] || match[2];
        const isEscaped = Boolean(match[1]);
        const isMissing = !isEscaped && isTagMissing(tagName);
        if (renderMissingTagsAsText && isMissing) {
          html += escapeHtml(match[0]);
        } else {
          html += buildTagSpanHtml(tagName, isEscaped);
        }
        lastIndex = tagRegex.lastIndex;
      }
      if (lastIndex < line.length) {
        html += escapeHtml(line.substring(lastIndex));
      }
      
      // Add <br> for newlines (except after the last line)
      if (lineIndex < lines.length - 1) {
        html += '<br>';
      }
    });
    
    return html;
  };

  const parseFromDom = (el) => {
    if (!el) return '';
    
    // Recursive function to properly handle nested elements and preserve newlines
    const extractText = (node) => {
      let result = '';
      
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.nodeValue;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();
        
        // Handle tag chips
        if (node.getAttribute && node.getAttribute('data-tag')) {
          const tagName = node.getAttribute('data-tag');
          const isEscaped = node.getAttribute('data-escaped') === 'true' || node.classList?.contains('tag-chip-escaped');
          return isEscaped ? `{{${tagName}}}` : `{${tagName}}`;
        }
        // Handle <br> elements - convert to newline
        else if (tagName === 'br') {
          result += '\n';
        }
        // Handle block elements (div, p) - add newline before content if not first
        else if (tagName === 'div' || tagName === 'p') {
          // Check if this div/p has actual content
          const hasContent = node.childNodes.length > 0;
          if (hasContent) {
            // Add newline before block element (except if it's the first child or previous sibling already added one)
            const prevSibling = node.previousSibling;
            const isFirstChild = !prevSibling;
            const prevEndsWithNewline = result.endsWith('\n');
            
            if (!isFirstChild && !prevEndsWithNewline) {
              result += '\n';
            }
            
            // Process children
            node.childNodes.forEach((child) => {
              result += extractText(child);
            });
          }
        }
        // Handle other elements - just process children
        else {
          node.childNodes.forEach((child) => {
            result += extractText(child);
          });
        }
      }
      
      return result;
    };
    
    let text = '';
    el.childNodes.forEach((node) => {
      text += extractText(node);
    });
    
    return normalizeForMode(text);
  };

  const getSelectedEditorText = () => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection || !selection.rangeCount) return '';
    const range = selection.getRangeAt(0);
    const startsInEditor = root.contains(range.startContainer);
    const endsInEditor = root.contains(range.endContainer);
    if (!startsInEditor || !endsInEditor) return '';

    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    return parseFromDom(wrapper);
  };

  const hasRawTagPattern = (el) => {
    if (!el) return false;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentNode && node.parentNode.closest && node.parentNode.closest('.tag-chip')) continue;
      if (tagPattern.test(node.nodeValue || '')) return true;
    }
    return false;
  };

  const getCaretOffset = () => {
    const root = rootRef.current;
    if (!root) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().length;
  };

  const restoreCaretFromMarker = () => {
    const root = rootRef.current;
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const value = node.nodeValue || '';
      const idx = value.indexOf(caretMarker);
      if (idx === -1) continue;
      const before = value.slice(0, idx);
      const after = value.slice(idx + caretMarker.length);
      node.nodeValue = before + after;
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.setStart(node, before.length);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    // Fallback: place caret at end
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Expose API to parent
  useImperativeHandle(ref, () => ({
    insertTag: (tagName) => insertTagAtCursor(tagName),
    focus: () => !isDisabled && rootRef.current && rootRef.current.focus(),
    getValue: () => parseFromDom(rootRef.current)
  }), [isDisabled]);

  useEffect(() => {
    if (!rootRef.current) return;
    const currentText = parseFromDom(rootRef.current);
    const needToRebuild = currentText !== displayValue || tagDefinitionSignatureRef.current !== tagLookupSignature;
    if (needToRebuild) {
      rootRef.current.innerHTML = toHtml(displayValue);
    }
    setIsEmpty(!hasVisibleEditorContent(rootRef.current, displayValue));
    tagDefinitionSignatureRef.current = tagLookupSignature;
  }, [displayValue, tagLookupSignature]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handleInput = () => {
      if (isDisabled) {
        return;
      }
      if (isApplyingRef.current) {
        isApplyingRef.current = false;
        return;
      }
      const text = parseFromDom(root);
      const normalizedText = isBlankText(text) ? '' : text;
      onChange && onChange(normalizedText);
      setIsEmpty(!hasVisibleEditorContent(root, normalizedText));

      if (isBlankText(normalizedText)) {
        if (root.innerHTML !== '') {
          isApplyingRef.current = true;
          root.innerHTML = '';
        }
        return;
      }

      if (hasRawTagPattern(root)) {
        const caretOffset = getCaretOffset();
        const safeOffset = typeof caretOffset === 'number'
          ? Math.min(Math.max(caretOffset, 0), normalizedText.length)
          : normalizedText.length;
        const markedText = `${normalizedText.slice(0, safeOffset)}${caretMarker}${normalizedText.slice(safeOffset)}`;
        const html = toHtml(markedText);
        if (root.innerHTML !== html) {
          isApplyingRef.current = true;
          root.innerHTML = html;
          restoreCaretFromMarker();
        }
      }
    };
    const handleClick = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const el = e.target.closest && e.target.closest('.tag-chip');
      if (el && el.dataset && el.dataset.tag) {
        e.preventDefault();
        e.stopPropagation();
        // Close any hover popover first
        setHoverAnchorEl(null);
        setHoveredTag(null);
        setTagActionAnchorEl(el);
        setTagActionTarget({
          tagName: el.dataset.tag,
          node: el,
          escaped: el.dataset.escaped === 'true'
        });
        return;
      }
      saveSelection();
    };
  const handleMouseOver = (e) => {
    if (isDisabled) {
      return;
    }
    if (tagActionAnchorEl || escapeDialogOpen || renameDialogOpen) {
      return;
    }
    const chip = e.target.closest && e.target.closest('.tag-chip');
    if (chip && chip.dataset && chip.dataset.tag) {
      const tagName = chip.dataset.tag;
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
          // Check cache first
          const cacheKey = `${caseid}:${tagName.toLowerCase()}`;
          const cached = tagPreviewCacheRef.current.get(cacheKey);
          if (cached) {
            setHoveredTag(cached);
            setHoverAnchorEl(chip);
            setHoverLoading(false);
            return;
          }
          // Request from backend
          if (caseid && sendMessage) {
            setHoverAnchorEl(chip);
            setHoveredTag({ tagName, loading: true });
            setHoverLoading(true);
            sendMessage({ command: 'getTagPreview', caseid, variable: tagName });
          }
        }, 400);
      } else {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      }
    };
  const handleMouseOut = (e) => {
    if (isDisabled) {
      return;
    }
      const chip = e.target.closest && e.target.closest('.tag-chip');
      if (!chip) {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        setHoverAnchorEl(null);
        setHoveredTag(null);
        setHoverLoading(false);
      }
    };
    const handlePaste = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      const text = normalizeForMode((e.clipboardData || window.clipboardData).getData('text'));
      const standaloneTag = getStandalonePastedTag(text);
      if (standaloneTag && (standaloneTag.escaped || !isTagMissing(standaloneTag.tagName))) {
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;
        selection.deleteFromDocument();
        const range = selection.getRangeAt(0);
        const span = buildTagSpan(standaloneTag.tagName, standaloneTag.escaped);
        range.insertNode(span);
        range.setStartAfter(span);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        handleInput();
        return;
      }

      const pasteText = trimStandalonePastedTag(text);
      const html = toHtml(pasteText, { renderMissingTagsAsText: true });
      
      // Use insertHTML to preserve the tag chips structure
      if (document.queryCommandSupported('insertHTML')) {
        document.execCommand('insertHTML', false, html);
      } else {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        const div = document.createElement('div');
        div.innerHTML = html;
        const fragment = document.createDocumentFragment();
        while (div.firstChild) {
          fragment.appendChild(div.firstChild);
        }
        selection.getRangeAt(0).insertNode(fragment);
        selection.collapseToEnd();
      }
      handleInput();
    };
    const handleCopy = (e) => {
      if (isDisabled) {
        return;
      }
      const selectedText = getSelectedEditorText();
      if (!selectedText) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', selectedText);
    };
    const handleCut = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      const selectedText = getSelectedEditorText();
      if (!selectedText) return;
      e.preventDefault();
      e.clipboardData.setData('text/plain', selectedText);
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;
      selection.deleteFromDocument();
      handleInput();
    };
    const handleKeyDown = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter' && isSingleLine) {
        e.preventDefault();
      }

      saveSelection();
      
      // Open tag menu when typing '{'
      if (e.key === '{' && normalizedTags.length > 0 && !tagMenuAnchorEl) {
        // We use setTimeout to allow the '{' character to be inserted first,
        // then we capture the selection right after it.
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0 && rootRef.current && rootRef.current.contains(sel.anchorNode)) {
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
            rootRef.current.blur();
            setTagMenuAnchorEl(rootRef.current);
            setTriggeredByBrace(true);
          }
        }, 0);
      }
    };
    const handleBeforeInput = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (!isSingleLine) return;
      if (e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak') {
        e.preventDefault();
      }
    };

    const handleKeyUp = () => {
      saveSelection();
    };

    const handleMouseUp = () => {
      saveSelection();
    };

    // Save current selection state
    const saveSelection = () => {
       const sel = window.getSelection();
       if (sel && sel.rangeCount > 0 && root && root.contains(sel.anchorNode)) {
          savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
       }
    };
    
    root.addEventListener('input', handleInput);
    root.addEventListener('click', handleClick);
    root.addEventListener('paste', handlePaste);
    root.addEventListener('copy', handleCopy);
    root.addEventListener('cut', handleCut);
    root.addEventListener('keydown', handleKeyDown);
    root.addEventListener('keyup', handleKeyUp);
    root.addEventListener('mouseup', handleMouseUp);
    root.addEventListener('beforeinput', handleBeforeInput);
    root.addEventListener('mouseover', handleMouseOver);
    root.addEventListener('mouseout', handleMouseOut);
    return () => {
      root.removeEventListener('input', handleInput);
      root.removeEventListener('click', handleClick);
      root.removeEventListener('paste', handlePaste);
      root.removeEventListener('copy', handleCopy);
      root.removeEventListener('cut', handleCut);
      root.removeEventListener('keydown', handleKeyDown);
      root.removeEventListener('keyup', handleKeyUp);
      root.removeEventListener('mouseup', handleMouseUp);
      root.removeEventListener('beforeinput', handleBeforeInput);
      root.removeEventListener('mouseover', handleMouseOver);
      root.removeEventListener('mouseout', handleMouseOut);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, [onChange, isSingleLine, normalizedTags.length, tagMenuAnchorEl, caseid, sendMessage, isDisabled]);

  // Subscribe to getTagPreview responses
  useEffect(() => {
    if (!subscribeToMessages) return;
    const unsubscribe = subscribeToMessages(['getTagPreview'], (message) => {
      const data = message.data || {};
      if (data.status !== 'success' || !data.variable) return;
      const cacheKey = `${data.caseid}:${data.variable.toLowerCase()}`;
      const previewData = {
        tagName: data.variable,
        tag_type: data.tag_type,
        label: data.label || data.variable,
        content: data.content || '',
        type: String(data.type || 'text').trim().toLowerCase() || 'text',
        samples: data.samples || [],
        totalCount: data.totalCount || 0
      };
      tagPreviewCacheRef.current.set(cacheKey, previewData);
      // If we're still hovering on this tag, update the display
      setHoveredTag((prev) => {
        if (prev && prev.tagName && prev.tagName.toLowerCase() === data.variable.toLowerCase()) {
          setHoverLoading(false);
          return previewData;
        }
        return prev;
      });
    });
    return unsubscribe;
  }, [subscribeToMessages]);

  const closeEscapeDialog = () => {
    setEscapeDialogOpen(false);
    setEscapeTarget(null);
  };

  const openRenameDialog = () => {
    if (!tagActionTarget?.node || !tagActionTarget?.tagName) {
      closeTagActionMenu();
      return;
    }

    setRenameValue(tagActionTarget.tagName);
    setRenameError('');
    setRenameTarget(tagActionTarget);
    setRenameDialogOpen(true);
    setTagActionAnchorEl(null);
  };

  const closeRenameDialog = () => {
    setRenameDialogOpen(false);
    setRenameValue('');
    setRenameError('');
    setRenameTarget(null);
    setTagActionAnchorEl(null);
    setTagActionTarget(null);
  };

  const closeMissingTagDialog = useCallback(() => {
    setMissingTagDialogOpen(false);
    setMissingTagValue('');
    preserveBraceSelectionRef.current = false;
    setTagMenuAnchorEl(null);
    setTriggeredByBrace(false);
  }, []);

  const buildTagSpan = (tagName, escaped = false) => {
    const span = document.createElement('span');
    span.className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    span.setAttribute('data-tag', tagName);
    if (escaped) {
      span.setAttribute('data-escaped', 'true');
    }
    span.setAttribute('contenteditable', 'false');
    const color = generateSaturatedColor(tagName);
    if (escaped) {
      span.style.backgroundColor = 'transparent';
      span.style.color = color;
      span.style.border = `1px dashed ${color}`;
    } else {
      span.style.backgroundColor = generatePastelColor(tagName);
      span.style.color = color;
    }
    const missing = !escaped && isTagMissing(tagName);
    if (missing) {
      span.setAttribute('data-missing-tag', 'true');
    }
    const labelSpan = document.createElement('span');
    labelSpan.className = 'tag-chip-label';
    labelSpan.textContent = `{${tagName}}`;
    span.appendChild(labelSpan);
    if (missing) {
      const warningText = getMissingTagTooltip(tagName);
      const warningIcon = document.createElement('span');
      warningIcon.className = 'tag-chip-warning';
      warningIcon.setAttribute('title', warningText);
      warningIcon.setAttribute('aria-label', warningText);
      warningIcon.textContent = '!';
      span.appendChild(warningIcon);
    }
    return span;
  };

  const handleEscapeConfirm = () => {
    if (!escapeTarget?.node || !escapeTarget?.tagName) {
      closeEscapeDialog();
      return;
    }

    const { tagName, node, escaped } = escapeTarget;
    replaceTagNode(node, tagName, !escaped);
    closeEscapeDialog();
  };

  const handleDeleteTag = () => {
    if (!tagActionTarget?.node) {
      closeTagActionMenu();
      return;
    }

    removeTagNode(tagActionTarget.node);
    clearTagActionTarget();
    closeTagActionMenu();
  };

  const handleRenameTag = () => {
    if (!tagActionTarget?.node || !tagActionTarget?.tagName) {
      closeTagActionMenu();
      return;
    }

    openRenameDialog();
  };

  const handleRenameConfirm = () => {
    const nextTagName = String(renameValue || '').trim();
    if (!renameTarget?.node || !renameTarget?.tagName) {
      closeRenameDialog();
      return;
    }

    if (!nextTagName || !tagNamePattern.test(nextTagName)) {
      setRenameError(t('tag_editor.rename_invalid', 'Tag names can only contain letters, numbers, and underscores.'));
      return;
    }

    replaceTagNode(renameTarget.node, nextTagName, Boolean(renameTarget.escaped));
    closeRenameDialog();
  };

  const insertTagAtCursor = (tagName) => {
    if (isDisabled) {
      return;
    }
    const sel = window.getSelection();
    if (!sel || !sel.getRangeAt || !rootRef.current) return;
    
    let range;
    
    // Check if current selection is valid and inside editor
    if (sel.rangeCount > 0 && rootRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      range = sel.getRangeAt(0);
    }
    // If not, try to use saved selection
    else if (savedSelectionRef.current) {
       range = savedSelectionRef.current;
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    // If still no valid range inside editor, fallback to end
    if (!range || !rootRef.current.contains(range.commonAncestorContainer)) {
       rootRef.current.focus();
       range = document.createRange();
       range.selectNodeContents(rootRef.current);
       range.collapse(false);
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    const span = buildTagSpan(tagName);
    range.deleteContents();
    range.insertNode(span);
    // Position caret after the inserted text node
    const newRange = document.createRange();
    newRange.setStartAfter(span);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    
    // Trigger change - manually dispatch input event so handleInput runs
    if (rootRef.current) {
      const event = new Event('input', { bubbles: true });
      rootRef.current.dispatchEvent(event);
    }
  };

  const removeLeadingBraceForTriggeredSelection = useCallback(() => {
    if (!triggeredByBrace || !savedSelectionRef.current) {
      return;
    }

    try {
      const sel = window.getSelection();
      if (!sel) {
        return;
      }
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);

      const range = sel.getRangeAt(0);
      if (range.startOffset > 0) {
        range.setStart(range.startContainer, range.startOffset - 1);
        range.deleteContents();
      }
    } catch (err) {
      console.warn('Failed to remove brace', err);
    } finally {
      setTriggeredByBrace(false);
    }
  }, [triggeredByBrace]);

  const insertResolvedTag = useCallback((tagName) => {
    if (!tagName) {
      closeMissingTagDialog();
      return;
    }

    removeLeadingBraceForTriggeredSelection();
    insertTagAtCursor(tagName);
    setTagMenuAnchorEl(null);
    closeMissingTagDialog();
  }, [closeMissingTagDialog, insertTagAtCursor, removeLeadingBraceForTriggeredSelection]);

  useEffect(() => {
    if (tagActionAnchorEl || escapeDialogOpen || renameDialogOpen) {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      setHoverAnchorEl(null);
      setHoveredTag(null);
      setHoverLoading(false);
    }
  }, [tagActionAnchorEl, escapeDialogOpen, renameDialogOpen]);

  const handleInternalTagClick = (event) => {
    setTagMenuAnchorEl(event.currentTarget);
  };

  const handleInternalTagSelect = (tagValue) => {
    insertResolvedTag(tagValue);
  };

  const showTagButton = !isDisabled && (onTagClick || normalizedTags.length > 0);

  return (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <Box
        component="div"
        ref={rootRef}
        className={className}
        contentEditable={!isDisabled}
        suppressContentEditableWarning
        spellCheck={!isDisabled}
        sx={{
        width: '100%',
        minWidth: 0,
        minHeight: computedMinHeight,
        maxHeight: computedMaxHeight,
        borderRadius: '12px',
        border: '1px solid #ddd',
        backgroundColor: isDisabled ? 'action.disabledBackground' : 'transparent',
        color: isDisabled ? 'text.disabled' : 'text.primary',
        cursor: isDisabled ? 'not-allowed' : 'text',
        p: editorPadding,
        pr: showTagButton ? 5 : editorPadding,
        '&:focus': { outline: isDisabled ? 'none' : `2px solid rgba(87,87,176,0.1)` },
        whiteSpace: isSingleLine ? 'nowrap' : 'pre-wrap',
        overflowX: isSingleLine ? 'auto' : 'hidden',
        overflowY: isSingleLine ? 'hidden' : 'auto',
        '& .tag-chip': {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          marginRight: '6px'
        },
        '& .tag-chip[data-missing-tag="true"]': {
          paddingRight: '14px'
        },
        '& .tag-chip-escaped': {
          backgroundColor: 'transparent'
        },
        '& .tag-chip-label': {
          display: 'inline-block'
        },
        '& .tag-chip-warning': {
          position: 'absolute',
          top: '-4px',
          right: '-4px',
          width: '18px',
          height: '18px',
          borderRadius: '999px',
          backgroundColor: '#d32f2f',
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
          letterSpacing: '0.05em'
        }
      }}
    />
      {isEmpty && placeholder && (
        <Box
          onClick={() => {
            if (!isDisabled && rootRef.current) rootRef.current.focus();
          }}
          sx={{
            position: 'absolute',
            left: 12,
            top: isSingleLine ? 8 : 12,
            pointerEvents: isDisabled ? 'none' : 'auto',
            color: isDisabled ? 'text.disabled' : 'rgba(0,0,0,0.38)',
            fontSize: '0.95rem',
            userSelect: 'none',
            cursor: isDisabled ? 'default' : 'text'
          }}
        >{placeholder}</Box>
      )}
      {showTagButton && (
        <Tooltip title="Insert tag">
          <IconButton
            onMouseDown={(e) => {
              // Try to prevent focus loss from editor when clicking button
              e.preventDefault();
            }}
            onClick={onTagClick || handleInternalTagClick}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: isSingleLine ? '50%' : 8,
              transform: isSingleLine ? 'translateY(-50%)' : 'none',
              color: 'action.active',
              zIndex: 1
            }}
          >
            <LocalOfferIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {normalizedTags.length > 0 && (
        <TagsDropdown
          tags={normalizedTags}
          handleTagSelect={handleInternalTagSelect}
          onMissingTagSelect={(tagValue) => {
            if (!tagValue) return;
            preserveBraceSelectionRef.current = true;
            setMissingTagValue(tagValue);
            setMissingTagDialogOpen(true);
          }}
          anchorEl={tagMenuAnchorEl}
          setAnchorEl={(el) => {
            setTagMenuAnchorEl(el);
            if (!el && !preserveBraceSelectionRef.current) setTriggeredByBrace(false);
          }}
          scope={scope}
        />
      )}
      <Menu
        anchorEl={tagActionAnchorEl}
        open={Boolean(tagActionAnchorEl && tagActionTarget)}
        onClose={() => {
          closeTagActionMenu();
          setTagActionTarget(null);
        }}
        disableAutoFocusItem
      >
        <MenuItem
          onClick={handleDeleteTag}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t('tag_editor.delete_tag', 'Delete tag')}
        </MenuItem>
        <MenuItem
          onClick={handleRenameTag}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t('tag_editor.rename_tag', 'Rename tag')}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!tagActionTarget?.node) {
              closeTagActionMenu();
              return;
            }
            setEscapeTarget(tagActionTarget);
            setEscapeDialogOpen(true);
            closeTagActionMenu();
          }}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon>
            <TextFieldsIcon fontSize="small" />
          </ListItemIcon>
          {tagActionTarget?.escaped
            ? t('tag_editor.convert_to_tag', 'Convert to tag')
            : t('tag_editor.convert_to_text', 'Convert to text')}
        </MenuItem>
      </Menu>
      <Dialog open={escapeDialogOpen} onClose={closeEscapeDialog}>
        <DialogTitle>
          {escapeTarget?.escaped
            ? t('tag_editor.unescape_title', 'Convert back to a tag?')
            : t('tag_editor.escape_title', 'Keep this tag as plain text?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_body', 'This will turn the plain text back into a real tag so it can be replaced with data.')
              : t('tag_editor.escape_body', 'If you choose “Keep as Text,” this tag will stay exactly as you typed it and will not be replaced with real data.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEscapeDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleEscapeConfirm} variant="contained">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_confirm', 'Convert to Tag')
              : t('tag_editor.escape_confirm', 'Keep as Text')}
          </Button>
          </DialogActions>
      </Dialog>
      <Dialog open={renameDialogOpen} onClose={closeRenameDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('tag_editor.rename_dialog_title', 'Rename tag')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('tag_editor.rename_dialog_label', 'New tag name')}
            value={renameValue}
            onChange={(event) => {
              setRenameValue(event.target.value);
              if (renameError) setRenameError('');
            }}
            error={Boolean(renameError)}
            helperText={renameError || t('tag_editor.rename_dialog_helper', 'Use only letters, numbers, and underscores.')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenameDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleRenameConfirm} variant="contained">
            {t('tag_editor.rename_confirm', 'Rename')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={missingTagDialogOpen} onClose={closeMissingTagDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('tag_editor.missing_tag_dialog_title', 'Tag does not exist')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('tag_editor.missing_tag_dialog_body', 'This tag does not exist. Do you want to insert it anyway?')}
          </Typography>
          <Box sx={{ mt: 1.5, p: 1, borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.03)', fontFamily: 'monospace', fontSize: '0.9rem' }}>
            {`{${missingTagValue}}`}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMissingTagDialog}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => insertResolvedTag(missingTagValue)} variant="contained">
            {t('tag_editor.missing_tag_dialog_confirm', 'Insert anyway')}
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={Boolean(hoverAnchorEl) && Boolean(hoveredTag)}
        anchorEl={hoverAnchorEl}
        onClose={() => { setHoverAnchorEl(null); setHoveredTag(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ pointerEvents: 'none', mt: 0.5 }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            maxWidth: 420,
            maxHeight: 320,
            overflow: 'auto',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            p: 0
          },
          onMouseLeave: () => { setHoverAnchorEl(null); setHoveredTag(null); setHoverLoading(false); }
        }}
      >
        {hoveredTag && (() => {
          if (hoveredTag.loading || hoverLoading) {
            return (
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: '#5757b0' }} />
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('tag_editor.loading_preview', 'Loading...')}
                </Typography>
              </Box>
            );
          }

          const content = String(hoveredTag.content || '');
          const label = hoveredTag.label || hoveredTag.tagName;
          const isTimeevent = hoveredTag.tag_type === 'timeevent';
          const isSystemEvent = hoveredTag.tag_type === 'system_event';
          const samples = hoveredTag.samples || [];
          const totalCountRaw = hoveredTag.totalCount;
          const totalCount = Number.isFinite(totalCountRaw) ? totalCountRaw : 0;
          const hasTotal = Number.isFinite(totalCountRaw) && totalCountRaw > 0;

          if ((isTimeevent || isSystemEvent) && samples.length === 0) {
            return (
              <Box sx={{ p: 0, minWidth: 200 }}>
                <Box sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'rgba(87,87,176,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                    {t('tag_editor.event_tag', 'Event Tag')}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    {t('tag_editor.no_data', 'No data')}
                  </Typography>
                </Box>
              </Box>
            );
          }

          if ((isTimeevent || isSystemEvent) && samples.length > 0) {
            const isSample = hasTotal ? totalCount > samples.length : true;
            return (
              <Box sx={{ p: 0, width: 360 }}>
                <Box sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'rgba(87,87,176,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                    {t('tag_editor.event_tag', 'Event Tag')}
                  </Typography>
                </Box>
                {isSample && (
                  <Box sx={{ px: 1.5, py: 0.5, backgroundColor: 'rgba(255,152,0,0.08)', borderBottom: '1px solid rgba(255,152,0,0.15)' }}>
                    <Typography variant="caption" sx={{ color: '#e65100', fontSize: '0.7rem', fontWeight: 500 }}>
                      ⚠ {hasTotal
                        ? t('tag_editor.sample_banner', 'Showing {{shown}} of {{total}} events (sample only)', { shown: samples.length, total: totalCount })
                        : t('tag_editor.sample_banner_unknown', 'Showing a sample of recent events')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ p: 1.5 }}>
                  {samples.map((sample, idx) => (
                    <Box key={idx} sx={{
                      py: 0.5,
                      px: 1,
                      mb: 0.5,
                      backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                      borderRadius: '4px'
                    }}>
                      <Typography variant="body2" sx={{
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                        color: '#333',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {sample}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }

          const isSystem = hoveredTag.tag_type === 'system' || hoveredTag.tag_type === 'system_event';

          if (isSystem) {
            return (
              <Box sx={{ p: 0, minWidth: 200 }}>
                <Box sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'rgba(87,87,176,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                    {isSystemEvent
                      ? t('tag_editor.event_tag', 'Event Tag')
                      : t('tag_editor.system_tag', 'System Tag')}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: content ? '#333' : '#999', fontStyle: content ? 'normal' : 'italic' }}>
                    {content || t('tag_editor.no_data_yet', 'No data yet')}
                  </Typography>
                </Box>
              </Box>
            );
          }

          if (!content) {
            return (
              <Box sx={{ p: 0, minWidth: 200 }}>
                <Box sx={{
                  px: 1.5, py: 0.75,
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'rgba(87,87,176,0.05)'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
                    {label}
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    {t('tag_editor.no_data', 'No data')}
                  </Typography>
                </Box>
              </Box>
            );
          }

          return (
            <Box sx={{ p: 0, width: 400 }}>
              <Box sx={{
                px: 1.5, py: 0.75,
                borderBottom: '1px solid #eee',
                backgroundColor: 'rgba(87,87,176,0.05)'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#5757b0', fontSize: '0.7rem' }}>
                  {label}
                </Typography>
              </Box>
              <Box sx={{ height: 250, minHeight: 100 }}>
                <UnifiedAnswerRenderer
                  tag={hoveredTag}
                  value={content}
                  widgetId={`tag-preview-${hoveredTag.tagName}`}
                  height={250}
                />
              </Box>
            </Box>
          );
        })()}
      </Popover>
    </Box>
  );
});

export default TagEditor;
